import { useCallback, useMemo, useState } from "react";
import { collectDescendantBodyUuids } from "./helpers";
import { isSceneGraphSiteNode } from "./gestures";

const REPARENT_DRAG_HOLD_MS = 300;
const REPARENT_DRAG_TRAVEL_PX = 10;
const REPARENT_WORLD_TARGET = "__world__";

/**
 * Owns scene-graph reparenting through the drag/drop gesture and the
 * context-menu list/world actions.
 */
export function useSceneGraphReparentPick({
  bodies,
  bodyParentMap,
  isPhysicsRunning,
  interactionLayerRef,
  runGroupedBodyAction,
  sceneActions,
  clearBatchSelection,
  closeContextMenu,
  contextMenuBodyUuids,
  contextMenuBodyUuidSet,
  effectiveBatchBodyUuids,
  effectiveBatchBodyUuidSet,
  effectiveBatchMeshIds,
  effectiveBatchMeshIdSet,
  effectiveBatchGeomKeys,
  effectiveBatchGeomKeySet,
  effectiveBatchSiteIds,
  effectiveBatchSiteIdSet,
  effectiveBatchInertialKeys,
  effectiveBatchInertialKeySet,
  effectiveBatchJointIds,
  effectiveBatchJointIdSet,
  handleMoveMeshesToBody,
  handleMoveGeomsToBody,
  handleMoveSitesToBody,
  handleMoveCamerasToBody,
  handleMoveInertialsToBody,
  handleMoveJointsToBody,
  getJointReparentTargetDisabledReason,
}) {
  const [reparentPick, setReparentPick] = useState(null);
  const [reparentDragHoverBodyUuid, setReparentDragHoverBodyUuid] = useState(null);
  const [reparentDragGhost, setReparentDragGhost] = useState(null);

  const getTopLevelBodyUuids = useCallback(
    (bodyUuids, selectedSet) =>
      bodyUuids.filter((bodyUuid) => {
        let parentUuid = bodyParentMap[bodyUuid] || null;
        while (parentUuid) {
          if (selectedSet.has(parentUuid)) return false;
          parentUuid = bodyParentMap[parentUuid] || null;
        }
        return true;
      }),
    [bodyParentMap]
  );

  const effectiveMoveBodyUuids = useMemo(
    () => getTopLevelBodyUuids(contextMenuBodyUuids, contextMenuBodyUuidSet),
    [contextMenuBodyUuids, contextMenuBodyUuidSet, getTopLevelBodyUuids]
  );

  const reparentPickMoveSet = useMemo(
    () => new Set(reparentPick?.moveBodyUuids ?? []),
    [reparentPick]
  );

  const getInvalidParentUuidsForPick = useCallback(
    (pick) => {
      if (pick?.kind === "body" && pick.moveBodyUuids?.length) {
        return collectDescendantBodyUuids(pick.moveBodyUuids, bodies || {});
      }
      if (pick?.kind === "joint" && pick.jointIds?.length) {
        return new Set(
          Object.values(bodies || {})
            .filter(
              (body) =>
                body?.uuid && getJointReparentTargetDisabledReason?.(body.uuid, pick.jointIds)
            )
            .map((body) => body.uuid)
        );
      }
      if (pick?.kind === "inertial" && pick.inertialKeys?.length === 1) {
        const sourceBodyUuid = String(pick.inertialKeys[0] || "").split(":")[0] || null;
        return new Set(
          Object.values(bodies || {})
            .filter((body) => body?.uuid && body.inertia && body.uuid !== sourceBodyUuid)
            .map((body) => body.uuid)
        );
      }
      return new Set();
    },
    [bodies, getJointReparentTargetDisabledReason]
  );

  const reparentPickInvalidParentUuids = useMemo(
    () => getInvalidParentUuidsForPick(reparentPick),
    [getInvalidParentUuidsForPick, reparentPick]
  );

  const moveBodiesToParentTarget = useCallback(
    (moveBodyUuids, targetParentUuid) => {
      if (isPhysicsRunning || !Array.isArray(moveBodyUuids) || moveBodyUuids.length === 0) return;
      const bodiesToMove = moveBodyUuids.filter(
        (bodyUuid) => (bodyParentMap[bodyUuid] || null) !== targetParentUuid
      );
      if (bodiesToMove.length === 0) {
        clearBatchSelection();
        return;
      }
      runGroupedBodyAction(() => {
        bodiesToMove.forEach((bodyUuid) => {
          sceneActions.moveBodyToParent(bodyUuid, targetParentUuid);
        });
      });
      clearBatchSelection();
    },
    [isPhysicsRunning, bodyParentMap, runGroupedBodyAction, sceneActions, clearBatchSelection]
  );

  const handleMoveBodies = useCallback(
    (targetParentUuid) => {
      moveBodiesToParentTarget(effectiveMoveBodyUuids, targetParentUuid);
    },
    [effectiveMoveBodyUuids, moveBodiesToParentTarget]
  );

  const applyReparentPick = useCallback(
    (pick, targetParentUuid) => {
      if (!pick) return;
      if (pick.kind !== "body" && !targetParentUuid) return;
      if (pick.kind === "body") {
        if (!pick.moveBodyUuids?.length) return;
        moveBodiesToParentTarget([...pick.moveBodyUuids], targetParentUuid);
        return;
      }
      if (pick.kind === "mesh") {
        handleMoveMeshesToBody?.(targetParentUuid, [...(pick.meshIds || [])]);
      } else if (pick.kind === "geom") {
        handleMoveGeomsToBody?.(targetParentUuid, [...(pick.geomKeys || [])]);
      } else if (pick.kind === "site") {
        handleMoveSitesToBody?.(targetParentUuid, [...(pick.siteIds || [])]);
      } else if (pick.kind === "camera") {
        handleMoveCamerasToBody?.(targetParentUuid, [...(pick.cameraIds || [])]);
      } else if (pick.kind === "inertial") {
        handleMoveInertialsToBody?.(targetParentUuid, [...(pick.inertialKeys || [])]);
      } else if (pick.kind === "joint") {
        handleMoveJointsToBody?.(targetParentUuid, [...(pick.jointIds || [])]);
      }
    },
    [
      moveBodiesToParentTarget,
      handleMoveMeshesToBody,
      handleMoveGeomsToBody,
      handleMoveSitesToBody,
      handleMoveCamerasToBody,
      handleMoveInertialsToBody,
      handleMoveJointsToBody,
    ]
  );

  const cancelReparentDrag = useCallback(() => {
    setReparentPick(null);
    setReparentDragHoverBodyUuid(null);
    setReparentDragGhost(null);
  }, []);

  const getGeomKeyForNode = useCallback((node) => {
    if (!node?.parentBodyUuid || !node.uuid) return null;
    return `${node.parentBodyUuid}:${node.uuid}`;
  }, []);

  const getReparentPickForNode = useCallback(
    (node) => {
      if (isPhysicsRunning || reparentPick || !node) return null;
      if (node.type === "body" && node.uuid) {
        const bodyUuids = effectiveBatchBodyUuidSet.has(node.uuid)
          ? effectiveBatchBodyUuids
          : [node.uuid];
        const moveBodyUuids = getTopLevelBodyUuids(bodyUuids, new Set(bodyUuids));
        return moveBodyUuids.length ? { kind: "body", moveBodyUuids } : null;
      }
      if (node.type === "mesh") {
        if (node.meshId) {
          const meshIds = effectiveBatchMeshIdSet.has(node.meshId)
            ? effectiveBatchMeshIds
            : [node.meshId];
          return meshIds.length ? { kind: "mesh", meshIds } : null;
        }
        const geomKey = getGeomKeyForNode(node);
        if (!geomKey) return null;
        const geomKeys = effectiveBatchGeomKeySet.has(geomKey) ? effectiveBatchGeomKeys : [geomKey];
        return geomKeys.length ? { kind: "geom", geomKeys } : null;
      }
      if (node.type === "geom") {
        const geomKey = getGeomKeyForNode(node);
        if (!geomKey) return null;
        const geomKeys = effectiveBatchGeomKeySet.has(geomKey) ? effectiveBatchGeomKeys : [geomKey];
        return geomKeys.length ? { kind: "geom", geomKeys } : null;
      }
      if (isSceneGraphSiteNode(node) && node.uuid) {
        const siteIds = effectiveBatchSiteIdSet.has(node.uuid)
          ? effectiveBatchSiteIds
          : [node.uuid];
        return siteIds.length ? { kind: "site", siteIds } : null;
      }
      if (node.type === "camera" && node.uuid) {
        return { kind: "camera", cameraIds: [node.uuid] };
      }
      if (node.type === "inertial") {
        const inertialKey = getGeomKeyForNode(node);
        if (!inertialKey) return null;
        const inertialKeys = effectiveBatchInertialKeySet.has(inertialKey)
          ? effectiveBatchInertialKeys
          : [inertialKey];
        return inertialKeys.length === 1 ? { kind: "inertial", inertialKeys } : null;
      }
      if (node.type === "joint" && node.uuid) {
        const jointIds = effectiveBatchJointIdSet.has(node.uuid)
          ? effectiveBatchJointIds
          : [node.uuid];
        return jointIds.length ? { kind: "joint", jointIds } : null;
      }
      return null;
    },
    [
      isPhysicsRunning,
      reparentPick,
      effectiveBatchBodyUuidSet,
      effectiveBatchBodyUuids,
      getTopLevelBodyUuids,
      effectiveBatchMeshIdSet,
      effectiveBatchMeshIds,
      getGeomKeyForNode,
      effectiveBatchGeomKeySet,
      effectiveBatchGeomKeys,
      effectiveBatchSiteIdSet,
      effectiveBatchSiteIds,
      effectiveBatchInertialKeySet,
      effectiveBatchInertialKeys,
      effectiveBatchJointIdSet,
      effectiveBatchJointIds,
    ]
  );

  const handleReparentDragPointerDown = useCallback(
    (node, event) => {
      if (!event?.isPrimary || event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey)
        return;
      const pick = getReparentPickForNode(node);
      if (!pick) return;

      const startX = event.clientX;
      const startY = event.clientY;
      let active = true;
      let activated = false;
      let latestHoverBodyUuid = null;
      const invalidParentUuids = getInvalidParentUuidsForPick(pick);
      const ghostNode = {
        type: node.type === "attachment_site" ? "site" : node.type,
        name: node.displayName ?? node.name ?? "",
      };

      const updateGhost = (clientX, clientY) => {
        const svg = interactionLayerRef.current?.querySelector?.("svg");
        const panGroup = interactionLayerRef.current?.querySelector?.(
          "[data-scene-graph-content-pan-group='true']"
        );
        const ctm = panGroup?.getScreenCTM?.();
        if (!svg || typeof svg.createSVGPoint !== "function" || !ctm) return;
        const point = svg.createSVGPoint();
        point.x = clientX;
        point.y = clientY;
        const graphPoint = point.matrixTransform(ctm.inverse());
        setReparentDragGhost({
          x: graphPoint.x,
          y: graphPoint.y,
          type: ghostNode.type,
          name: ghostNode.name,
        });
      };

      const getTargetBodyUuidAtPoint = (clientX, clientY) => {
        const element = document.elementFromPoint(clientX, clientY);
        const worldTarget = element?.closest?.("[data-scene-reparent-world-target='true']");
        if (worldTarget && pick.kind === "body") return REPARENT_WORLD_TARGET;
        const target = element?.closest?.("[data-scene-reparent-target-body-uuid]");
        const bodyUuid = target?.getAttribute?.("data-scene-reparent-target-body-uuid") || null;
        if (!bodyUuid || invalidParentUuids.has(bodyUuid)) return null;
        return bodyUuid;
      };

      const updateHover = (clientX, clientY) => {
        const bodyUuid = getTargetBodyUuidAtPoint(clientX, clientY);
        latestHoverBodyUuid = bodyUuid;
        setReparentDragHoverBodyUuid((current) => (current === bodyUuid ? current : bodyUuid));
        updateGhost(clientX, clientY);
      };

      const activate = () => {
        if (!active || activated) return;
        activated = true;
        event.preventDefault();
        event.stopPropagation();
        setReparentPick(pick);
        closeContextMenu();
        updateHover(startX, startY);
      };
      const timer = window.setTimeout(activate, REPARENT_DRAG_HOLD_MS);
      const cleanup = () => {
        active = false;
        window.clearTimeout(timer);
        setReparentDragHoverBodyUuid(null);
        setReparentDragGhost(null);
        window.removeEventListener("pointermove", onMove, true);
        window.removeEventListener("pointerup", onPointerUp, true);
        window.removeEventListener("pointercancel", onPointerCancel, true);
      };
      const onMove = (moveEvent) => {
        if (moveEvent.pointerId !== event.pointerId) return;
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;
        if (Math.hypot(dx, dy) >= REPARENT_DRAG_TRAVEL_PX) {
          activate();
        }
        if (activated) {
          moveEvent.preventDefault();
          moveEvent.stopPropagation();
          updateHover(moveEvent.clientX, moveEvent.clientY);
        }
      };
      const onPointerUp = (upEvent) => {
        if (upEvent.pointerId !== event.pointerId) return;
        if (activated) {
          upEvent.preventDefault();
          upEvent.stopPropagation();
          const targetBodyUuid =
            getTargetBodyUuidAtPoint(upEvent.clientX, upEvent.clientY) || latestHoverBodyUuid;
          setReparentPick(null);
          if (targetBodyUuid) {
            applyReparentPick(
              pick,
              targetBodyUuid === REPARENT_WORLD_TARGET ? null : targetBodyUuid
            );
          }
        }
        cleanup();
      };
      const onPointerCancel = (cancelEvent) => {
        if (cancelEvent.pointerId !== event.pointerId) return;
        setReparentPick(null);
        cleanup();
      };
      window.addEventListener("pointermove", onMove, true);
      window.addEventListener("pointerup", onPointerUp, true);
      window.addEventListener("pointercancel", onPointerCancel, true);
    },
    [
      applyReparentPick,
      closeContextMenu,
      getInvalidParentUuidsForPick,
      getReparentPickForNode,
      interactionLayerRef,
    ]
  );

  return {
    reparentPick,
    reparentDragHoverBodyUuid,
    reparentDragGhost,
    effectiveMoveBodyUuids,
    reparentPickMoveSet,
    reparentPickInvalidParentUuids,
    handleMoveBodies,
    cancelReparentDrag,
    handleReparentDragPointerDown,
  };
}
